import {
  BanknoteIcon,
  Clock,
  CreditCardIcon,
  GiftIcon,
  LandmarkIcon,
  QrCodeIcon,
  ReceiptIcon,
  StarIcon,
  WalletIcon,
} from "lucide-react"
import { useTranslation } from "react-i18next"

import { Button } from "@/components/ui/button"
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet"
import { PaymentMethodNameEnum } from "@/enums"
import { CycleStatusEnum } from "@/enums/subscriptions"
import { DateHelper } from "@/utils"

import {
  getCycleStatusInfo,
  isCancelPaymentMethodByType,
} from "./SubscriptionsHelper"

const PAYMENT_METHOD_ICONS: Record<
  PaymentMethodNameEnum,
  React.ElementType
> = {
  [PaymentMethodNameEnum.BALANCE]: WalletIcon,
  [PaymentMethodNameEnum.CREDIT_CARD]: CreditCardIcon,
  [PaymentMethodNameEnum.DEBIT_CARD]: CreditCardIcon,
  [PaymentMethodNameEnum.LOYALTY_POINTS]: StarIcon,
  [PaymentMethodNameEnum.GIFT_CARD_VTEX]: GiftIcon,
  [PaymentMethodNameEnum.OPEN_FINANCE]: LandmarkIcon,
  [PaymentMethodNameEnum.BANK_SLIP]: ReceiptIcon,
  [PaymentMethodNameEnum.BOLEPIX]: ReceiptIcon,
  [PaymentMethodNameEnum.DIRECT_DEBIT]: LandmarkIcon,
  [PaymentMethodNameEnum.NUPAY]: BanknoteIcon,
  [PaymentMethodNameEnum.PIX]: QrCodeIcon,
  [PaymentMethodNameEnum.PIX_AUTO]: QrCodeIcon,
  [PaymentMethodNameEnum.CREDIT_CARD_PRE_APPROVAL]:
    CreditCardIcon,
}

type EventItem = {
  status: string
  description: string
  createdAt: string
}

interface CycleEventsSheetProps {
  cycleNumber: number
  events?: EventItem[]
  paymentMethod?: string
}

export const CycleEventsSheet = ({
  cycleNumber,
  events = [],
  paymentMethod,
}: CycleEventsSheetProps) => {
  const { t } = useTranslation()
  const hasEvents = events.length > 0
  const isCancelPaymentMethod = isCancelPaymentMethodByType(
    paymentMethod as PaymentMethodNameEnum
  )

  if (!hasEvents) return null

  return (
    <Sheet>
      <SheetTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="h-auto gap-1 p-0 text-xs text-muted-foreground transition-colors hover:bg-transparent hover:text-foreground"
        >
          <Clock size={14} />
          {t("common.events")}
        </Button>
      </SheetTrigger>
      <SheetContent
        side="bottom"
        className="flex max-h-[70vh] flex-col rounded-t-xl"
      >
        <SheetHeader className="flex-shrink-0 pb-4">
          <SheetTitle className="flex items-center gap-2">
            {t("subscriptions.details.cycles")} -{" "}
            {t("subscriptions.table.cycle")} {cycleNumber}
          </SheetTitle>
          {paymentMethod && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <span>{t("common.paymentMethod")}:</span>
              <div className="flex items-center gap-1.5">
                {(() => {
                  const Icon =
                    PAYMENT_METHOD_ICONS[
                      paymentMethod as PaymentMethodNameEnum
                    ]
                  return Icon ? (
                    <Icon size={14} className="shrink-0" />
                  ) : null
                })()}
                <span className="font-medium">
                  {t(
                    `common.paymentMethods.${paymentMethod}`
                  )}
                </span>
              </div>
            </div>
          )}
        </SheetHeader>

        <div className="min-h-0 flex-1 space-y-0 overflow-y-auto pb-4 pr-2">
          {events.map((event, index) => {
            const isLast = index === events.length - 1
            const statusInfo = getCycleStatusInfo(
              event.status as CycleStatusEnum,
              t
            )

            const eventDescription =
              isCancelPaymentMethod &&
              event.status === CycleStatusEnum.REVERSED
                ? event.description?.replaceAll(
                    /estornado/gi,
                    "cancelado"
                  )
                : event.description

            return (
              <div key={index} className="flex gap-3">
                <div className="flex flex-col items-center">
                  <div
                    className="h-3 w-3 rounded-full"
                    style={{
                      backgroundColor: statusInfo.bgColor,
                    }}
                  />
                  {!isLast && (
                    <div className="h-full w-0.5 bg-border" />
                  )}
                </div>
                <div className="flex-1 pb-4">
                  <p className="text-sm font-medium">
                    {eventDescription}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {DateHelper.formatDateTime(
                      event.createdAt,
                      "dd/MM/yyyy HH:mm"
                    )}
                  </p>
                </div>
              </div>
            )
          })}
        </div>
      </SheetContent>
    </Sheet>
  )
}
